// @ts-strict-ignore
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { ShortVideo } from "../types";

interface ShortPreviewDialogProps {
  open: boolean;
  short: ShortVideo | null;
  onClose: () => void;
}

export const ShortPreviewDialog: React.FC<ShortPreviewDialogProps> = ({
  open,
  short,
  onClose,
}) => {
  if (!short) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Short Video Commerce Preview
          </Typography>
          <IconButton size="small" onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
          {/* Full Video Display Container (Uncropped, Natural Dimensions) */}
          <Box
            style={{
              position: "relative",
              maxWidth: "100%",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.25), 0 10px 10px -5px rgba(0, 0, 0, 0.2)",
              border: "4px solid #1e293b",
              backgroundColor: "#000000",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {short.videoUrl ? (
              <video
                src={short.videoUrl}
                poster={short.thumbnailUrl || undefined}
                controls
                autoPlay
                loop
                playsInline
                style={{
                  maxWidth: "100%",
                  maxHeight: "65vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <Box
                style={{
                  width: 320,
                  height: 480,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#0f172a",
                  color: "#94a3b8",
                  padding: 24,
                  textAlign: "center",
                }}
              >
                <PlayCircleOutlineIcon style={{ fontSize: 48, marginBottom: 12 }} />
                <Text color="default2">No video stream URL available</Text>
              </Box>
            )}
          </Box>

          {/* Video Details & Storefront Info Card */}
          <Box
            width="100%"
            padding={3}
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {/* Title & Subtitle */}
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="h6" style={{ fontWeight: 700, color: "#1e293b" }}>
                {short.title}
              </Typography>
              {short.subtitle && (
                <Typography variant="body2" color="textSecondary">
                  {short.subtitle}
                </Typography>
              )}
            </Box>

            {/* Grid Information */}
            <Box
              display="grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
              gap={2}
            >
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Status
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={short.isPublished ? "Active on Storefront" : "Hidden (Draft)"}
                    style={{
                      backgroundColor: short.isPublished ? "#dcfce7" : "#fee2e2",
                      color: short.isPublished ? "#15803d" : "#b91c1c",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Duration
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {short.durationSeconds ? `${short.durationSeconds} seconds` : "—"}
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Target Platform
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={short.targetPlatform === "APP" ? "Mobile App" : short.targetPlatform === "WEB" ? "Web App" : "All Platforms"}
                    style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Target Device
                </Typography>
                <Box>
                  <Chip
                    size="small"
                    label={short.targetDevice === "MOBILE" ? "Mobile" : short.targetDevice === "TABLET" ? "Tablet" : short.targetDevice === "DESKTOP" ? "Desktop" : "All Devices"}
                    style={{ backgroundColor: "#f3e8ff", color: "#7e22ce", fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.5} style={{ minWidth: 0 }}>
                <Typography variant="caption" color="textSecondary">
                  Redirect Action
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    wordBreak: "break-all",
                    overflowWrap: "anywhere",
                  }}
                >
                  {short.redirectType}: {short.redirectValue || "None"}
                </Typography>
              </Box>

              {short.videoUrl && (
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="caption" color="textSecondary">
                    Direct Video Asset
                  </Typography>
                  <a
                    href={short.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#2563eb",
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      textDecoration: "none",
                      width: "fit-content",
                    }}
                  >
                    <span>Stream Video File</span>
                    <OpenInNewIcon style={{ fontSize: 18 }} />
                  </a>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        <Button variant="secondary" onClick={onClose}>
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShortPreviewDialog;
